// Tape draw tool
// Click to set anchor → move mouse to preview → click to commit segment
// Right-click or Escape to cancel

const tapeApp = (() => {
    const tool        = document.getElementById('tape-tool');
    const canvas      = document.getElementById('tape-canvas');
    const segments    = document.getElementById('tape-segments');
    const defsEl      = document.getElementById('tape-defs');
    const previewPoly = document.getElementById('tape-preview-poly');
    const anchorEl    = document.getElementById('tape-anchor');
    const undoBtn     = document.getElementById('tape-undo');
    const clearBtn    = document.getElementById('tape-clear');
    const closeBtn         = document.getElementById('tape-close');
    const downloadSvgBtn   = document.getElementById('tape-download-svg');
    const downloadJpgBtn   = document.getElementById('tape-download-jpg');
    const patternModeBtn    = document.getElementById('tape-mode-pattern');
    const textModeBtn       = document.getElementById('tape-mode-text');
    const textInputEl       = document.getElementById('tape-text-input');
    const bgColorPicker     = document.getElementById('tape-bg-color');
    const textColorPicker   = document.getElementById('tape-text-color');
    const previewPatBg      = document.getElementById('tape-preview-pat-bg');
    const previewTextPat    = document.getElementById('tape-preview-text-pat');
    const previewTextPatBg  = document.getElementById('tape-preview-text-pat-bg');
    const previewTextPatEl  = document.getElementById('tape-preview-text-pat-el');
    const widthSlider       = document.getElementById('tape-width-slider');
    const widthVal         = document.getElementById('tape-width-val');
    const resolutionSelect = document.getElementById('tape-resolution');
    const frameRect        = document.getElementById('tape-frame');
    const previewPat       = document.getElementById('tape-preview-pat');
    const previewPatImg    = document.getElementById('tape-preview-pat-img');
    const frameBgImage     = document.getElementById('tape-frame-bg');
    const bgImageInput     = document.getElementById('tape-bg-image');
    const bgImageLabel     = document.getElementById('tape-bg-image-label');

    if (!tool) return;

    // Admin setting from tape snippet; end users cannot change this in the UI.
    const textLetterSpacingRaw = tool.getAttribute('data-text-letter-spacing');
    const parsedTextLetterSpacing = Number.parseFloat(textLetterSpacingRaw);
    const textLetterSpacing = Number.isFinite(parsedTextLetterSpacing) ? parsedTextLetterSpacing : 0;

    // ——— Frame / resolution ———

    let frameRes = null; // { w, h } of the selected export resolution
    let bgImageSrc = null; // base64 data URI of the uploaded background image

    function getFrameRect() {
        if (!frameRes) return null;
        const vw  = canvas.clientWidth;
        const vh  = canvas.clientHeight;
        const pad = 80;
        const s   = Math.min((vw - pad * 2) / frameRes.w, (vh - pad * 2) / frameRes.h);
        const fw  = frameRes.w * s;
        const fh  = frameRes.h * s;
        return { x: (vw - fw) / 2, y: (vh - fh) / 2, w: fw, h: fh };
    }

    function updateFrame() {
        const r = getFrameRect();
        if (!r) {
            frameRect.setAttribute('opacity', '0');
            frameBgImage.setAttribute('opacity', '0');
            bgImageLabel.hidden = true;
            return;
        }
        frameRect.setAttribute('x',       r.x);
        frameRect.setAttribute('y',       r.y);
        frameRect.setAttribute('width',   r.w);
        frameRect.setAttribute('height',  r.h);
        frameRect.setAttribute('opacity', '1');
        frameBgImage.setAttribute('x',      r.x);
        frameBgImage.setAttribute('y',      r.y);
        frameBgImage.setAttribute('width',  r.w);
        frameBgImage.setAttribute('height', r.h);
        if (bgImageSrc) frameBgImage.setAttribute('opacity', '1');
        bgImageLabel.hidden = false;
    }

    resolutionSelect.addEventListener('change', () => {
        const val = resolutionSelect.value;
        if (!val) { frameRes = null; updateFrame(); return; }
        const [w, h] = val.split(':').map(Number);
        frameRes = { w, h };
        updateFrame();
    });

    bgImageInput.addEventListener('change', () => {
        const file = bgImageInput.files[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = () => {
            bgImageSrc = reader.result;
            frameBgImage.setAttribute('href', bgImageSrc);
            const r = getFrameRect();
            if (r) frameBgImage.setAttribute('opacity', '1');
        };
        reader.readAsDataURL(file);
    });

    window.addEventListener('resize', updateFrame);

    // Read config from CSS variables set in _tape.scss
    const styles = getComputedStyle(document.documentElement);
    let tapeWidth = parseInt(styles.getPropertyValue('--tape-width').trim(), 10) || 48;
    let tapeBg    = bgColorPicker.value;
    let textColor = textColorPicker.value;

    // SVG pattern config
    const imgSrc  = '/assets/img/pattern.svg';
    const fontSrc = '/assets/fonts/Plakat-Black.woff2';
    let patternH = tapeWidth;
    let patternW = (3893 / 391) * patternH; // scale SVG to tape height

    // Sync slider to the CSS-configured initial value
    widthSlider.value       = tapeWidth;
    widthVal.textContent    = tapeWidth;

    widthSlider.addEventListener('input', () => {
        tapeWidth = parseInt(widthSlider.value, 10);
        patternH  = tapeWidth;
        patternW  = (3893 / 391) * patternH;
        widthVal.textContent = tapeWidth;
    });

    // Initialise preview backgrounds and keep them in sync with the picker
    previewPatBg.setAttribute('fill', tapeBg);
    previewTextPatBg.setAttribute('fill', tapeBg);
    previewTextPatEl.setAttribute('fill', textColor);
    previewTextPatEl.setAttribute('letter-spacing', textLetterSpacing);

    bgColorPicker.addEventListener('input', () => {
        tapeBg = bgColorPicker.value;
        previewPatBg.setAttribute('fill', tapeBg);
        previewTextPatBg.setAttribute('fill', tapeBg);
    });

    textColorPicker.addEventListener('input', () => {
        textColor = textColorPicker.value;
        previewTextPatEl.setAttribute('fill', textColor);
    });

    let mode      = 'pattern'; // 'pattern' | 'text'
    let anchor    = null;
    let segCount  = 0;
    let phase     = 0;      // px consumed along the image pattern roll
    let textPhase = 0;      // px consumed along the text pattern roll
    let history   = [];     // { g, defEls, phaseBefore, textPhaseBefore }

    // ——— Geometry ———

    // Convert a mouse event to SVG coordinate space
    function toSVG(e) {
        const pt = canvas.createSVGPoint();
        pt.x = e.clientX;
        pt.y = e.clientY;
        return pt.matrixTransform(canvas.getScreenCTM().inverse());
    }

    // Build the four corners of a tape segment as an SVG points string
    function makePoints(x1, y1, x2, y2, w) {
        const dx  = x2 - x1;
        const dy  = y2 - y1;
        const len = Math.hypot(dx, dy);
        if (len < 2) return null;
        const nx = (-dy / len) * (w / 2);
        const ny = ( dx / len) * (w / 2);
        return `${x1 + nx},${y1 + ny} ${x1 - nx},${y1 - ny} ${x2 - nx},${y2 - ny} ${x2 + nx},${y2 + ny}`;
    }

    // Align an SVG <pattern>+<image> pair to a tape segment's direction.
    // phaseOffset shifts the tile origin so the pattern continues from the previous segment.
    function setPatternTransform(patEl, imgEl, x1, y1, x2, y2, phaseOffset = 0) {
        const angle = Math.atan2(y2 - y1, x2 - x1) * 180 / Math.PI;
        patEl.setAttribute('x', 0);
        patEl.setAttribute('y', 0);
        patEl.setAttribute('width',  patternW);
        patEl.setAttribute('height', patternH);
        // translate to segment start → rotate → shift back by phase and centre vertically
        patEl.setAttribute('patternTransform',
            `translate(${x1}, ${y1}) rotate(${angle}) translate(${-phaseOffset}, ${-patternH / 2})`);
        imgEl.setAttribute('width',  patternW);
        imgEl.setAttribute('height', patternH);
    }

    // Align a text <pattern> to a tape segment's direction
    function setTextPatternTransform(patEl, pw, ph, x1, y1, x2, y2, phaseOffset = 0) {
        const angle = Math.atan2(y2 - y1, x2 - x1) * 180 / Math.PI;
        patEl.setAttribute('x', 0);
        patEl.setAttribute('y', 0);
        patEl.setAttribute('width',  pw);
        patEl.setAttribute('height', ph);
        patEl.setAttribute('patternTransform',
            `translate(${x1}, ${y1}) rotate(${angle}) translate(${-phaseOffset}, ${-ph / 2})`);
    }

    // Measure the rendered width of the current text input at the current tapeWidth
    function measureText() {
        const text = textInputEl.value.trim();
        if (!text) return tapeWidth;
        const el = document.createElementNS('http://www.w3.org/2000/svg', 'text');
        el.setAttribute('font-family', 'Plakat, sans-serif');
        el.setAttribute('font-size', tapeWidth);
        el.setAttribute('letter-spacing', textLetterSpacing);
        el.setAttribute('opacity', '0');
        el.textContent = text;
        canvas.appendChild(el);
        const w = el.getComputedTextLength();
        canvas.removeChild(el);
        return w || tapeWidth;
    }

    // Reproduce the live preview's vertical text placement (y = ph/2 with
    // dominant-baseline='middle') as an explicit alphabetic baseline, which
    // Illustrator honours (it ignores dominant-baseline and would otherwise mis-
    // place the text). Both positions are measured in-browser so the exported
    // baseline matches the preview exactly, whatever the font's real metrics are.
    function textBaselineForMiddle(str, fontSize, ls, midY) {
        const measure = (baseline, y) => {
            const el = document.createElementNS('http://www.w3.org/2000/svg', 'text');
            el.setAttribute('font-family', 'Plakat, sans-serif');
            el.setAttribute('font-size', fontSize);
            el.setAttribute('letter-spacing', ls);
            el.setAttribute('dominant-baseline', baseline);
            el.setAttribute('x', 0);
            el.setAttribute('y', y);
            el.setAttribute('opacity', '0');
            el.textContent = str || 'x';
            canvas.appendChild(el);
            let box;
            try { box = el.getBBox(); } catch (_) { box = { y: 0, height: 0 }; }
            canvas.removeChild(el);
            return box;
        };
        // Glyph top relative to the baseline (alphabetic text sits its baseline at 0)
        // and glyph top as the preview positions it ('middle' at midY). The baseline
        // that reproduces the preview is the difference between the two tops.
        const alpha = measure('alphabetic', 0);
        const mid   = measure('middle', midY);
        const baseY = mid.y - alpha.y;
        return Number.isFinite(baseY) ? baseY : midY;
    }

    // ——— State helpers ———

    function commitText(x1, y1, x2, y2) {
        const text = textInputEl.value.trim();
        if (!text || Math.hypot(x2 - x1, y2 - y1) < 5) return;
        const pts = makePoints(x1, y1, x2, y2, tapeWidth);
        if (!pts) return;

        const tw    = measureText();
        const pw    = tw + tapeWidth * 0.5; // text width + spacing gap
        const ph    = tapeWidth;
        const id    = `tape-seg-${segCount++}`;
        const patId = `${id}-pat`;
        const g     = document.createElementNS('http://www.w3.org/2000/svg', 'g');
        const defEls = [];

        const pat    = document.createElementNS('http://www.w3.org/2000/svg', 'pattern');
        const bg     = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
        const textEl = document.createElementNS('http://www.w3.org/2000/svg', 'text');

        pat.setAttribute('id', patId);
        pat.setAttribute('patternUnits', 'userSpaceOnUse');
        bg.setAttribute('width',  pw);
        bg.setAttribute('height', ph);
        bg.setAttribute('fill',   tapeBg);
        pat.appendChild(bg);
        textEl.setAttribute('x', 0);
        textEl.setAttribute('y', ph / 2);
        textEl.setAttribute('font-family', 'Plakat, sans-serif');
        textEl.setAttribute('font-size', tapeWidth);
        textEl.setAttribute('letter-spacing', textLetterSpacing);
        textEl.setAttribute('dominant-baseline', 'middle');
        textEl.setAttribute('fill', textColor);
        textEl.textContent = text;
        pat.appendChild(textEl);
        const textPhaseBefore = textPhase;
        setTextPatternTransform(pat, pw, ph, x1, y1, x2, y2, textPhase);
        defsEl.appendChild(pat);
        defEls.push(pat);

        const poly = document.createElementNS('http://www.w3.org/2000/svg', 'polygon');
        poly.setAttribute('points', pts);
        poly.setAttribute('fill', `url(#${patId})`);
        g.appendChild(poly);

        // Advance textPhase so the next segment continues from where this one ended
        textPhase = (textPhase + Math.hypot(x2 - x1, y2 - y1)) % pw;

        // Stash geometry so the exporter can bake Illustrator-friendly tiling.
        g.setAttribute('data-mode',      'text');
        g.setAttribute('data-x1',        x1);
        g.setAttribute('data-y1',        y1);
        g.setAttribute('data-x2',        x2);
        g.setAttribute('data-y2',        y2);
        g.setAttribute('data-w',         tapeWidth);
        g.setAttribute('data-phase',     textPhaseBefore);
        g.setAttribute('data-bg',        tapeBg);
        g.setAttribute('data-pw',        pw);
        g.setAttribute('data-ph',        ph);
        g.setAttribute('data-text',      text);
        g.setAttribute('data-textcolor', textColor);
        g.setAttribute('data-ls',        textLetterSpacing);

        segments.appendChild(g);
        history.push({ g, defEls, phaseBefore: phase, textPhaseBefore });
    }

    function commitSegment(x1, y1, x2, y2) {
        const pts = makePoints(x1, y1, x2, y2, tapeWidth);
        if (!pts) return;

        const id     = `tape-seg-${segCount++}`;
        const g      = document.createElementNS('http://www.w3.org/2000/svg', 'g');
        const defEls = [];

        const poly   = document.createElementNS('http://www.w3.org/2000/svg', 'polygon');
        const patId  = `${id}-pat`;
        const pat    = document.createElementNS('http://www.w3.org/2000/svg', 'pattern');
        const patImg = document.createElementNS('http://www.w3.org/2000/svg', 'image');
        pat.setAttribute('id', patId);
        pat.setAttribute('patternUnits', 'userSpaceOnUse');
        const patRect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
        patRect.setAttribute('width',  patternW);
        patRect.setAttribute('height', patternH);
        patRect.setAttribute('fill', tapeBg);
        pat.appendChild(patRect);
        patImg.setAttribute('href', imgSrc);
        patImg.setAttribute('preserveAspectRatio', 'none');
        pat.appendChild(patImg);
        const phaseBefore = phase;
        setPatternTransform(pat, patImg, x1, y1, x2, y2, phase);
        defsEl.appendChild(pat);
        defEls.push(pat);

        poly.setAttribute('points', pts);
        poly.setAttribute('fill', `url(#${patId})`);
        g.appendChild(poly);

        // Advance phase so the next segment continues from where this one ended
        phase = (phase + Math.hypot(x2 - x1, y2 - y1)) % patternW;

        // Stash geometry so the exporter can bake Illustrator-friendly tiling.
        g.setAttribute('data-mode',  'pattern');
        g.setAttribute('data-x1',    x1);
        g.setAttribute('data-y1',    y1);
        g.setAttribute('data-x2',    x2);
        g.setAttribute('data-y2',    y2);
        g.setAttribute('data-w',     tapeWidth);
        g.setAttribute('data-phase', phaseBefore);
        g.setAttribute('data-bg',    tapeBg);

        segments.appendChild(g);
        history.push({ g, defEls, phaseBefore, textPhaseBefore: textPhase });
    }

    function showAnchor(x, y) {
        anchorEl.setAttribute('cx', x);
        anchorEl.setAttribute('cy', y);
        anchorEl.setAttribute('opacity', '1');
    }

    function endLine() {
        anchor = null;
        previewPoly.setAttribute('points', '');
        previewPoly.setAttribute('fill', 'transparent');
        previewPoly.setAttribute('opacity', '1');
        anchorEl.setAttribute('opacity', '0');
    }

    function undo() {
        if (!history.length) return;
        const { g, defEls, phaseBefore, textPhaseBefore } = history.pop();
        segments.removeChild(g);
        defEls.forEach(el => defsEl && defsEl.contains(el) && defsEl.removeChild(el));
        phase     = phaseBefore;
        textPhase = textPhaseBefore;
    }

    // ——— Canvas events ———

    canvas.addEventListener('mousemove', e => {
        if (!anchor) return;
        const p  = toSVG(e);
        const x1 = anchor.x, y1 = anchor.y, x2 = p.x, y2 = p.y;

        if (mode === 'text') {
            const pts = makePoints(x1, y1, x2, y2, tapeWidth);
            if (!pts) return;
            const tw = measureText();
            const pw = tw + tapeWidth * 0.5;
            const ph = tapeWidth;
            previewTextPatBg.setAttribute('width',  pw);
            previewTextPatBg.setAttribute('height', ph);
            previewTextPatBg.setAttribute('fill',   tapeBg);
            previewTextPatEl.setAttribute('y', ph / 2);
            previewTextPatEl.setAttribute('font-size', tapeWidth);
            previewTextPatEl.setAttribute('letter-spacing', textLetterSpacing);
            previewTextPatEl.setAttribute('fill', textColor);
            previewTextPatEl.textContent = textInputEl.value.trim() || 'text';
            setTextPatternTransform(previewTextPat, pw, ph, x1, y1, x2, y2, textPhase);
            previewPoly.setAttribute('points', pts);
            previewPoly.setAttribute('fill', 'url(#tape-preview-text-pat)');
            previewPoly.setAttribute('opacity', '0.65');
            return;
        }

        const pts = makePoints(x1, y1, x2, y2, tapeWidth);
        if (!pts) return;
        previewPoly.setAttribute('points', pts);
        setPatternTransform(previewPat, previewPatImg, x1, y1, x2, y2, phase);
        previewPoly.setAttribute('fill', 'url(#tape-preview-pat)');
        previewPoly.setAttribute('opacity', '0.65');
    });

    canvas.addEventListener('click', e => {
        const p = toSVG(e);
        if (!anchor) {
            anchor = p;
            showAnchor(p.x, p.y);
            return;
        }
        if (mode === 'text') {
            commitText(anchor.x, anchor.y, p.x, p.y);
        } else {
            commitSegment(anchor.x, anchor.y, p.x, p.y);
        }
        endLine();
    });

    // Right-click ends the current line without adding a segment
    canvas.addEventListener('contextmenu', e => {
        e.preventDefault();
        endLine();
    });

    canvas.addEventListener('mouseleave', () => {
        previewPoly.setAttribute('points', '');
    });

    // ——— Keyboard ———

    document.addEventListener('keydown', e => {
        if (!tool.classList.contains('is-active')) return;
        if (e.key === 'Escape') {
            endLine();
        }
        if ((e.metaKey || e.ctrlKey) && e.key === 'z') {
            e.preventDefault();
            undo();
        }
    });

    // ——— Controls ———

    undoBtn.addEventListener('click', e => {
        e.stopPropagation();
        undo();
    });

    clearBtn.addEventListener('click', e => {
        e.stopPropagation();
        segments.innerHTML = '';
        if (defsEl) {
            defsEl.innerHTML = '';
            defsEl.appendChild(previewPat);
            defsEl.appendChild(previewTextPat);
        }
        history = [];
        phase = 0;
        textPhase = 0;
        endLine();
    });

    closeBtn.addEventListener('click', e => {
        e.stopPropagation();
        tool.classList.remove('is-active');
        endLine();
    });

    patternModeBtn.addEventListener('click', e => {
        e.stopPropagation();
        mode = 'pattern';
        patternModeBtn.classList.add('is-active');
        textModeBtn.classList.remove('is-active');
        textInputEl.hidden = true;
        endLine();
    });

    textModeBtn.addEventListener('click', e => {
        e.stopPropagation();
        mode = 'text';
        textModeBtn.classList.add('is-active');
        patternModeBtn.classList.remove('is-active');
        textInputEl.hidden = false;
        endLine();
    });

    // ——— Shared helper: inline pattern.svg as Illustrator-friendly vector art ———
    // Illustrator cannot resolve an <image> that points at an embedded SVG data
    // URI (missing linked file + checkerboard) and it ignores <pattern>
    // patternTransform (so tiling/rotation/offset are lost). On export we inline
    // pattern.svg's paths once as a <g> (classes flattened to presentation
    // attributes, since Illustrator is unreliable with <style>) and bake the
    // tiling ourselves with plain <use> + clip paths.
    let patternArtCache = null; // { node, vbW, vbH }
    const patternArtId  = 'tape-pattern-art';

    async function getPatternArt() {
        if (patternArtCache) {
            return {
                node: patternArtCache.node.cloneNode(true),
                vbW:  patternArtCache.vbW,
                vbH:  patternArtCache.vbH,
            };
        }

        let svgText;
        try {
            svgText = await (await fetch(imgSrc)).text();
        } catch (_) {
            return null;
        }

        const doc    = new DOMParser().parseFromString(svgText, 'image/svg+xml');
        const srcSvg = doc.querySelector('svg');
        if (!srcSvg) return null;

        // Collect ".class { prop: val; ... }" rules, then drop the <style> blocks.
        const classRules = {};
        doc.querySelectorAll('style').forEach(styleEl => {
            const re = /\.([\w-]+)\s*\{([^}]*)\}/g;
            let m;
            while ((m = re.exec(styleEl.textContent)) !== null) {
                const decls = classRules[m[1]] || (classRules[m[1]] = {});
                m[2].split(';').forEach(decl => {
                    const i = decl.indexOf(':');
                    if (i === -1) return;
                    const prop = decl.slice(0, i).trim();
                    const val  = decl.slice(i + 1).trim();
                    if (prop && val) decls[prop] = val;
                });
            }
            styleEl.remove();
        });

        // Apply the collected rules as presentation attributes, then drop classes.
        doc.querySelectorAll('[class]').forEach(el => {
            el.getAttribute('class').split(/\s+/).forEach(cls => {
                const decls = classRules[cls];
                if (!decls) return;
                for (const prop in decls) {
                    if (!el.hasAttribute(prop)) el.setAttribute(prop, decls[prop]);
                }
            });
            el.removeAttribute('class');
        });

        // Native artwork dimensions, used to scale each tile to the tape size.
        let vbW, vbH;
        const vb = srcSvg.getAttribute('viewBox');
        if (vb) {
            const p = vb.split(/[\s,]+/).map(Number);
            vbW = p[2];
            vbH = p[3];
        } else {
            vbW = parseFloat(srcSvg.getAttribute('width'));
            vbH = parseFloat(srcSvg.getAttribute('height'));
        }

        const art = document.createElementNS('http://www.w3.org/2000/svg', 'g');
        art.setAttribute('id', patternArtId);
        Array.from(srcSvg.childNodes).forEach(node =>
            art.appendChild(document.importNode(node, true)));

        patternArtCache = { node: art, vbW, vbH };
        return { node: art.cloneNode(true), vbW, vbH };
    }

    // ——— Shared helper: build an export SVG element (tape segments only, no bg image) ———
    // Each tape is rebuilt as baked geometry — a clip path in the tape shape plus
    // repeated <use>/<text> tiles positioned with a plain group transform — so the
    // result needs neither <image> links nor <pattern> patternTransform, both of
    // which Illustrator mishandles.

    async function buildExportSVG() {
        const svgNS   = 'http://www.w3.org/2000/svg';
        const xlinkNS = 'http://www.w3.org/1999/xlink';

        const vw = canvas.clientWidth;
        const vh = canvas.clientHeight;

        let viewBox, exportW, exportH;
        const fr = getFrameRect();
        if (fr) {
            viewBox  = `${fr.x} ${fr.y} ${fr.w} ${fr.h}`;
            exportW  = frameRes.w;
            exportH  = frameRes.h;
        } else {
            viewBox  = `0 0 ${vw} ${vh}`;
            exportW  = vw;
            exportH  = vh;
        }

        const svgEl = document.createElementNS(svgNS, 'svg');
        svgEl.setAttribute('xmlns', svgNS);
        svgEl.setAttribute('xmlns:xlink', xlinkNS);
        svgEl.setAttribute('width',   exportW);
        svgEl.setAttribute('height',  exportH);
        svgEl.setAttribute('viewBox', viewBox);

        const defs = document.createElementNS(svgNS, 'defs');
        const root = document.createElementNS(svgNS, 'g');
        svgEl.appendChild(defs);
        svgEl.appendChild(root);

        const segs       = Array.from(segments.children);
        const hasPattern = segs.some(s => s.getAttribute('data-mode') === 'pattern');
        const hasText    = segs.some(s => s.getAttribute('data-mode') === 'text');

        // Inline the pattern artwork once; every pattern tile references it.
        let art = null;
        if (hasPattern) {
            art = await getPatternArt();
            if (art) defs.appendChild(art.node);
        }

        // Embed the font so browsers preview correctly; Illustrator uses the
        // system-installed Plakat (SVG @font-face is ignored there).
        if (hasText) {
            try {
                const fontResp    = await fetch(fontSrc);
                const fontBlob    = await fontResp.blob();
                const fontDataUri = await new Promise(resolve => {
                    const reader = new FileReader();
                    reader.onload = () => resolve(reader.result);
                    reader.readAsDataURL(fontBlob);
                });
                const style = document.createElementNS(svgNS, 'style');
                style.textContent = `@font-face { font-family: 'Plakat'; src: url('${fontDataUri}') format('woff2'); }`;
                defs.appendChild(style);
            } catch (_) {}
        }

        let clipSeq = 0;

        segs.forEach(seg => {
            const mode = seg.getAttribute('data-mode');
            const poly = seg.querySelector('polygon');
            if (!mode || !poly) return;

            const x1     = parseFloat(seg.getAttribute('data-x1'));
            const y1     = parseFloat(seg.getAttribute('data-y1'));
            const x2     = parseFloat(seg.getAttribute('data-x2'));
            const y2     = parseFloat(seg.getAttribute('data-y2'));
            const w      = parseFloat(seg.getAttribute('data-w'));
            const phase  = parseFloat(seg.getAttribute('data-phase')) || 0;
            const bg     = seg.getAttribute('data-bg') || tapeBg;
            const points = poly.getAttribute('points');
            const len    = Math.hypot(x2 - x1, y2 - y1);
            const angle  = Math.atan2(y2 - y1, x2 - x1) * 180 / Math.PI;

            // Clip everything to the tape's polygon.
            const id   = `tape-clip-${clipSeq++}`;
            const clip = document.createElementNS(svgNS, 'clipPath');
            clip.setAttribute('id', id);
            clip.setAttribute('clipPathUnits', 'userSpaceOnUse');
            const clipPoly = document.createElementNS(svgNS, 'polygon');
            clipPoly.setAttribute('points', points);
            clip.appendChild(clipPoly);
            defs.appendChild(clip);

            const clipped = document.createElementNS(svgNS, 'g');
            clipped.setAttribute('clip-path', `url(#${id})`);

            // Solid tape colour behind the tiled artwork.
            const bgPoly = document.createElementNS(svgNS, 'polygon');
            bgPoly.setAttribute('points', points);
            bgPoly.setAttribute('fill', bg);
            clipped.appendChild(bgPoly);

            // Local frame: start of the tape, rotated along it, shifted by the
            // running phase and vertically centred — mirrors the live pattern.
            const local = document.createElementNS(svgNS, 'g');
            local.setAttribute('transform',
                `translate(${x1} ${y1}) rotate(${angle}) translate(${-phase} ${-w / 2})`);

            if (mode === 'pattern' && art) {
                const tileW  = (3893 / 391) * w;
                const tileH  = w;
                const sx     = tileW / art.vbW;
                const sy     = tileH / art.vbH;
                const nTiles = Math.ceil((phase + len) / tileW) + 1;
                for (let i = 0; i < nTiles; i++) {
                    const use = document.createElementNS(svgNS, 'use');
                    use.setAttribute('href', `#${patternArtId}`);
                    use.setAttributeNS(xlinkNS, 'xlink:href', `#${patternArtId}`);
                    use.setAttribute('transform',
                        `translate(${i * tileW} 0) scale(${sx} ${sy})`);
                    local.appendChild(use);
                }
            } else if (mode === 'text') {
                const pw        = parseFloat(seg.getAttribute('data-pw')) || w;
                const ph        = parseFloat(seg.getAttribute('data-ph')) || w;
                const text      = seg.getAttribute('data-text') || '';
                const textcolor = seg.getAttribute('data-textcolor') || textColor;
                const ls        = parseFloat(seg.getAttribute('data-ls')) || 0;
                // Reproduce the live preview's vertical placement as an explicit
                // alphabetic baseline so browsers and Illustrator agree (the
                // latter ignores dominant-baseline='middle').
                const baseY  = textBaselineForMiddle(text, w, ls, ph / 2);
                const nTiles = Math.ceil((phase + len) / pw) + 1;
                for (let i = 0; i < nTiles; i++) {
                    const t = document.createElementNS(svgNS, 'text');
                    t.setAttribute('x', i * pw);
                    t.setAttribute('y', baseY);
                    t.setAttribute('font-family', 'Plakat, sans-serif');
                    t.setAttribute('font-size', w);
                    t.setAttribute('letter-spacing', ls);
                    t.setAttribute('dominant-baseline', 'alphabetic');
                    t.setAttribute('fill', textcolor);
                    t.textContent = text;
                    local.appendChild(t);
                }
            }

            clipped.appendChild(local);
            root.appendChild(clipped);
        });

        return { svgEl, exportW, exportH };
    }

    // ——— Download: SVG (tape only, no background image) ———

    downloadSvgBtn.addEventListener('click', async e => {
        e.stopPropagation();
        if (!segments.childElementCount) return;

        const { svgEl } = await buildExportSVG();

        const blob = new Blob(
            [new XMLSerializer().serializeToString(svgEl)],
            { type: 'image/svg+xml' }
        );
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = 'tape.svg';
        a.click();
        URL.revokeObjectURL(a.href);
    });

    // Match SVG's preserveAspectRatio='xMidYMid slice' behavior on canvas.
    function drawImageCover(ctx, img, destW, destH) {
        const imgW = img.naturalWidth || img.width;
        const imgH = img.naturalHeight || img.height;
        if (!imgW || !imgH) return;

        const scale = Math.max(destW / imgW, destH / imgH);
        const srcW = destW / scale;
        const srcH = destH / scale;
        const srcX = (imgW - srcW) / 2;
        const srcY = (imgH - srcH) / 2;

        ctx.drawImage(img, srcX, srcY, srcW, srcH, 0, 0, destW, destH);
    }

    // ——— Download: JPG (background image + tape, flattened) ———

    downloadJpgBtn.addEventListener('click', async e => {
        e.stopPropagation();
        if (!segments.childElementCount) return;

        const { svgEl, exportW, exportH } = await buildExportSVG();

        const svgBlob = new Blob(
            [new XMLSerializer().serializeToString(svgEl)],
            { type: 'image/svg+xml' }
        );
        const svgUrl = URL.createObjectURL(svgBlob);

        const offCanvas = document.createElement('canvas');
        offCanvas.width  = exportW;
        offCanvas.height = exportH;
        const ctx = offCanvas.getContext('2d');

        // Draw background image if one was uploaded
        if (bgImageSrc) {
            await new Promise(resolve => {
                const bgImg = new Image();
                bgImg.onload  = () => { drawImageCover(ctx, bgImg, exportW, exportH); resolve(); };
                bgImg.onerror = resolve;
                bgImg.src = bgImageSrc;
            });
        }

        // Draw tape SVG on top
        await new Promise(resolve => {
            const svgImg = new Image();
            svgImg.onload  = () => { ctx.drawImage(svgImg, 0, 0, exportW, exportH); resolve(); };
            svgImg.onerror = resolve;
            svgImg.src = svgUrl;
        });
        URL.revokeObjectURL(svgUrl);

        const a = document.createElement('a');
        a.href = offCanvas.toDataURL('image/jpeg', 0.92);
        a.download = 'tape.jpg';
        a.click();
    });

})();
